const stateKeys = {
  gigyaProfileStateKey: 'gigyaProfile',
  gigyaToken: 'gigyaToken',
  marlonToken: 'marlonToken',
  accountLanguage: 'language',
  languageChoice: 'language_choice',
  aaltraToken: 'aaltraToken',
  authenticateLauncherFlow: 'authenticateLauncherFlow',
  languageRestoreData: 'language_restore_data'
}

const initialState = () => ({
  lastRoute: '',
  pageHistory: [],
  page: 'index',
  previousPage: null,
  pageBackNavigation: false,
  gigyaReady: false,
  gigyaProfile: null,
  showGigyaLogin: false,
  loggedInEventReceived: false,
  loggedOutEventReceived: false,
  idToken: null,
  newAccessToken: false,
  runningAccessTokenInterval: false,
  locale: 'en-GB',
  fallbackLocale: 'en-GB',
  userAgent: null,
  isIOS: false,
  errorPage: false,
  customerPwa: true,
  authError: false,
  authenticateLauncherFlow: null,
  isHemsFilteredDetail: false,
  hasLiveTranslations: false
})

export const state = () => initialState()

export const mutations = {
  setIsHemsFilteredDetail(state, value) {
    state.isHemsFilteredDetail = value
  },
  setErrorPage(state, value) {
    state.errorPage = value
  },
  setLocale(state, locale) {
    state.locale = locale
  },
  setFallbackLocale(state, locale) {
    state.fallbackLocale = locale
  },
  setLoggedInEventReceived(state, value) {
    state.loggedInEventReceived = value
  },
  setLoggedOutEventReceived(state, value) {
    state.loggedOutEventReceived = value
  },
  setGigyaToken(state, value) {
    state.idToken = value
  },
  setHasNewAccessToken(state, value) {
    state.newAccessToken = value
  },
  setGigyaProfile(state, value) {
    state.gigyaProfile = value
  },
  setGigyaProfileFromStorage(state) {
    state.gigyaProfile = this.$storage.getUniversal(
      stateKeys.gigyaProfileStateKey
    )
  },
  setGigyaReady(state, value) {
    state.gigyaReady = value
  },
  setShowGigyaLogin(state, value) {
    state.showGigyaLogin = value
  },
  setPage(state, value) {
    state.page = value
    if (state.pageHistory.length > 1) {
      if (state.pageHistory[state.pageHistory.length - 2] === value) {
        state.pageHistory.pop()
        state.pageBackNavigation = true
        return
      }
    }
    state.pageBackNavigation = false
    state.pageHistory = state.pageHistory.concat(value)
    if (state.pageHistory.length > 1) {
      if (state.pageHistory[state.pageHistory.length - 2]) {
        state.previousPage = state.pageHistory[state.pageHistory.length - 2]
      }
    }
  },
  clearPageHistory(state) {
    state.pageHistory = []
  },
  setUserAgent(state) {
    state.userAgent = navigator.appVersion

    const iOSPossibilities = [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ]
    if (
      iOSPossibilities.includes(navigator.platform) ||
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    ) {
      state.isIOS = true
    }
  },
  setRunningAccessTokenInterval(state, value) {
    state.runningAccessTokenInterval = value
  },
  setCustomerPwa(state, value) {
    state.customerPwa = value
  },
  clearStorage(state) {
    const preserved = state.authenticateLauncherFlow
    const freshState = JSON.parse(JSON.stringify(initialState()))
    for (const key in freshState) {
      if (key !== stateKeys.authenticateLauncherFlow) {
        state[key] = freshState[key]
      }
    }
    state.authenticateLauncherFlow = preserved
  },
  setAuthError(state, value) {
    state.authError = value
  },
  setAuthenticateLauncherFlow(state, value) {
    state.authenticateLauncherFlow = value
  },
  setHasLiveTranslations(state, value) {
    state.hasLiveTranslations = value
  }
}

export const actions = {
  clearStorage({ commit }) {
    for (const prop in stateKeys) {
      // Don't clear languageRestoreData - we need it to compare after new login
      if (prop !== 'languageRestoreData') {
        this.$storage.removeUniversal(stateKeys[prop])
      }
    }
    commit('clearStorage')
  },
  async logout({ commit, dispatch }) {
    // console.log('logout')
    commit('clearPageHistory')
    await dispatch('maintenance/clearStorage', {}, { root: true })
    await dispatch('account/clearStorage', {}, { root: true })
    await dispatch('installation/clearStorage', {}, { root: true })
    await dispatch('package/clearStorage', {}, { root: true })
    await dispatch('registration/clearRegistration', {}, { root: true })
    await dispatch('service-partner/clearStorage', {}, { root: true })
    await dispatch('energy/logout', {}, { root: true })
  },
  getGigyaToken() {
    return this.$storage.getUniversal(stateKeys.gigyaToken)
  },
  setGigyaToken({ commit }, token) {
    commit('setGigyaToken', token)
    this.$storage.setUniversal(stateKeys.gigyaToken, token)
  },
  setIsAccessToken(_, value) {
    this.$storage.setUniversal(stateKeys.isAccessToken, value)
  },
  getLanguageChoice() {
    return this.$storage.getUniversal(stateKeys.languageChoice)
  },
  setLanguageChoice(_, value) {
    this.$storage.setUniversal(stateKeys.languageChoice, value)
  },
  getMarlonToken() {
    return this.$storage.getUniversal(stateKeys.marlonToken)
  },
  setMarlonToken(_, token) {
    if (token) {
      this.$storage.setUniversal(stateKeys.marlonToken, token)
    } else {
      this.$storage.removeUniversal(stateKeys.marlonToken)
    }
  },
  getGigyaProfile() {
    return this.$storage.getUniversal(stateKeys.gigyaProfileStateKey)
  },
  setGigyaProfile({ commit }, value) {
    this.$storage.setUniversal(stateKeys.gigyaProfileStateKey, value)
    commit('setGigyaProfile', value)

    // Check if we should restore languageChoice for the same user
    const languageRestoreData = this.$storage.getUniversal(
      stateKeys.languageRestoreData
    )

    if (
      languageRestoreData &&
      value?.email &&
      languageRestoreData.email === value.email
    ) {
      // Same user logged back in - restore their language choice
      if (languageRestoreData.languageChoice) {
        this.$storage.setUniversal(
          stateKeys.languageChoice,
          languageRestoreData.languageChoice
        )
      }
    }

    // Clear restore data after use
    this.$storage.removeUniversal(stateKeys.languageRestoreData)
  },
  setRunningAccessTokenInterval({ commit }, value) {
    commit('setRunningAccessTokenInterval', value)
  },
  getAuthenticateLauncherFlow() {
    return this.$storage.getUniversal(stateKeys.authenticateLauncherFlow)
  },
  setAuthenticateLauncherFlow({ commit }, value) {
    this.$storage.setUniversal(stateKeys.authenticateLauncherFlow, value)
    commit('setAuthenticateLauncherFlow', value)
  },
  async setLanguage({ state, commit }) {
    const languageChoice =
      this.$storage.getUniversal(stateKeys.languageChoice)?.language ||
      this.$storage.getUniversal(stateKeys.accountLanguage)
    const currentLocale = languageChoice || 'en-GB'

    if (!state.hasLiveTranslations) {
      const poeditorProjectTranslations = await this.$axiosPoeditor?.fetchTranslations(
        {
          id: '327213', // POEDITOR PROJECT ID
          language: currentLocale
        }
      )
      if (poeditorProjectTranslations) {
        this.$i18n.mergeLocaleMessage(currentLocale, {
          ...poeditorProjectTranslations
        })
        commit('setHasLiveTranslations', true)
      }
    }
  }
}
